import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Plus,
  Search,
  Eye,
  EyeOff,
  Star,
  StarOff,
  Edit3,
  Trash2,
  GripVertical,
  ChevronUp,
  ChevronDown,
  Loader2,
  X,
  AlertCircle,
  User,
  Phone,
  Mail,
  MessageCircle,
  ExternalLink,
  ImagePlus,
  Crown,
} from 'lucide-react';
import clsx from 'clsx';
import {
  useAdminTeam,
  useCreateTeamMember,
  useUpdateTeamMember,
  useDeleteTeamMember,
  useReorderTeamMembers,
  useToggleTeamVisibility,
  useToggleTeamFeatured,
  uploadTeamImage,
} from '../../services/teamService';
import { getImageUrl } from '../../lib/supabase';

const overlayVariants = { hidden: { opacity: 0 }, visible: { opacity: 1 }, exit: { opacity: 0 } };
const panelVariants = {
  hidden: { x: '100%' },
  visible: { x: 0, transition: { type: 'spring', damping: 30, stiffness: 300 } },
  exit: { x: '100%', transition: { duration: 0.2 } },
};

const EMPTY_FORM = {
  full_name: '',
  role: '',
  tagline: '',
  bio: '',
  phone: '',
  whatsapp: '',
  email: '',
  social_links: [],
  is_public: true,
  is_featured_on_home: false,
};

export default function AdminTeamPage() {
  const [search, setSearch] = useState('');
  const [editMember, setEditMember] = useState(null); // null or member object
  const [showCreate, setShowCreate] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteInput, setDeleteInput] = useState('');

  const { data: members, isLoading } = useAdminTeam();
  const visibilityMutation = useToggleTeamVisibility();
  const featuredMutation = useToggleTeamFeatured();
  const deleteMutation = useDeleteTeamMember();
  const reorderMutation = useReorderTeamMembers();

  const filtered = (members || []).filter((m) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      m.full_name?.toLowerCase().includes(q) ||
      m.role?.toLowerCase().includes(q) ||
      m.tagline?.toLowerCase().includes(q)
    );
  });

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    await deleteMutation.mutateAsync(deleteConfirm.id);
    setDeleteConfirm(null);
    setDeleteInput('');
  };

  const moveUp = (index) => {
    if (index === 0) return;
    const ids = filtered.map((m) => m.id);
    [ids[index - 1], ids[index]] = [ids[index], ids[index - 1]];
    reorderMutation.mutate(ids);
  };

  const moveDown = (index) => {
    if (index === filtered.length - 1) return;
    const ids = filtered.map((m) => m.id);
    [ids[index + 1], ids[index]] = [ids[index], ids[index + 1]];
    reorderMutation.mutate(ids);
  };

  const publicCount = (members || []).filter((m) => m.is_public).length;
  const featuredCount = (members || []).filter((m) => m.is_featured_on_home).length;

  const statCards = [
    { label: 'Total', value: members?.length || 0, icon: Users, bg: 'bg-blue-50', color: 'text-blue-600' },
    { label: 'Public', value: publicCount, icon: Eye, bg: 'bg-green-50', color: 'text-green-600' },
    { label: 'Featured', value: featuredCount, icon: Star, bg: 'bg-amber-50', color: 'text-amber-600' },
    { label: 'Hidden', value: (members?.length || 0) - publicCount, icon: EyeOff, bg: 'bg-red-50', color: 'text-red-600' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-brand-dark">Team Management</h1>
          <p className="text-sm text-brand-gray mt-1">Manage your team members and their profiles</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold bg-brand-red hover:bg-red-700 text-brand-white rounded-xl transition-colors"
        >
          <Plus size={14} /> Add Member
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {statCards.map((card, i) => {
          const Icon = card.icon;
          return (
            <motion.div
              key={card.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
              className="bg-brand-white border border-ui-border rounded-2xl p-4"
            >
              <div className="flex items-center gap-3">
                <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center', card.bg)}>
                  <Icon size={18} className={card.color} />
                </div>
                <div>
                  <p className="text-xs font-medium text-brand-gray">{card.label}</p>
                  <p className="text-xl font-extrabold text-brand-dark">{card.value}</p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Search + Member list */}
      <div className="bg-brand-white border border-ui-border rounded-2xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-6 py-4 border-b border-ui-border">
          <h3 className="text-sm font-bold text-brand-dark">All Members</h3>
          <div className="relative w-full sm:w-60">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted" />
            <input
              type="text"
              placeholder="Search members..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-8 pr-4 py-2 text-sm bg-brand-light border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
            />
          </div>
        </div>

        <div className="divide-y divide-ui-border">
          {isLoading ? (
            <div className="p-8 flex items-center justify-center">
              <Loader2 size={24} className="animate-spin text-brand-red" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Users size={40} className="text-ui-muted mx-auto mb-3" />
              <p className="text-sm font-bold text-brand-dark">No team members found</p>
              <p className="text-xs text-brand-gray mt-1">
                {search ? 'Try a different search' : 'Add your first team member'}
              </p>
            </div>
          ) : (
            filtered.map((member, i) => (
              <TeamMemberRow
                key={member.id}
                member={member}
                index={i}
                total={filtered.length}
                onEdit={() => setEditMember(member)}
                onDelete={() => setDeleteConfirm(member)}
                onToggleVisibility={() =>
                  visibilityMutation.mutate({ id: member.id, is_public: !member.is_public })
                }
                onToggleFeatured={() =>
                  featuredMutation.mutate({ id: member.id, is_featured_on_home: !member.is_featured_on_home })
                }
                onMoveUp={() => moveUp(i)}
                onMoveDown={() => moveDown(i)}
              />
            ))
          )}
        </div>
      </div>

      {/* Create slide-over */}
      <TeamFormSlideOver
        open={showCreate}
        onClose={() => setShowCreate(false)}
        mode="create"
      />

      {/* Edit slide-over */}
      <TeamFormSlideOver
        open={!!editMember}
        onClose={() => setEditMember(null)}
        mode="edit"
        member={editMember}
      />

      {/* Delete confirm */}
      <AnimatePresence>
        {deleteConfirm && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => { setDeleteConfirm(null); setDeleteInput(''); }}
              className="fixed inset-0 z-[60] bg-brand-dark/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] w-full max-w-sm bg-brand-white rounded-2xl shadow-2xl p-6"
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                  <AlertCircle size={20} className="text-error" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-brand-dark">Delete Team Member</h3>
                  <p className="text-xs text-brand-gray mt-1">
                    Type <strong>{deleteConfirm.full_name}</strong> to confirm deletion.
                  </p>
                </div>
              </div>
              <input
                type="text"
                value={deleteInput}
                onChange={(e) => setDeleteInput(e.target.value)}
                placeholder="Type member name..."
                className="w-full px-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted mb-4"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending || deleteInput !== deleteConfirm.full_name}
                  className={clsx(
                    'flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-bold rounded-xl transition-colors',
                    deleteInput === deleteConfirm.full_name
                      ? 'text-brand-white bg-error hover:bg-red-700'
                      : 'text-brand-gray bg-brand-light cursor-not-allowed'
                  )}
                >
                  {deleteMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  Delete
                </button>
                <button
                  onClick={() => { setDeleteConfirm(null); setDeleteInput(''); }}
                  className="px-4 py-2.5 text-sm font-bold text-brand-gray bg-brand-light hover:bg-gray-200 rounded-xl transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Team Member Row ── */
function TeamMemberRow({
  member,
  index,
  total,
  onEdit,
  onDelete,
  onToggleVisibility,
  onToggleFeatured,
  onMoveUp,
  onMoveDown,
}) {
  const imageUrl = member.image_path ? getImageUrl(member.image_path) : null;
  const isOwner = member.role?.toLowerCase().includes('owner') || member.role?.toLowerCase().includes('founder');

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: index * 0.02 }}
      className="p-4 sm:p-5 hover:bg-brand-light/30 transition-colors"
    >
      <div className="flex items-center gap-3">
        {/* Reorder controls */}
        <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
          <button
            onClick={onMoveUp}
            disabled={index === 0}
            className="p-0.5 rounded text-brand-gray hover:text-brand-dark disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label="Move up"
          >
            <ChevronUp size={14} />
          </button>
          <GripVertical size={14} className="text-ui-muted" />
          <button
            onClick={onMoveDown}
            disabled={index === total - 1}
            className="p-0.5 rounded text-brand-gray hover:text-brand-dark disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label="Move down"
          >
            <ChevronDown size={14} />
          </button>
        </div>

        {/* Image */}
        <div className="w-12 h-12 rounded-xl overflow-hidden bg-brand-light flex-shrink-0">
          {imageUrl ? (
            <img src={imageUrl} alt={member.full_name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <User size={20} className="text-ui-muted" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-brand-dark">{member.full_name}</span>
            {isOwner && (
              <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-brand-red/10 text-brand-red font-bold rounded-md">
                <Crown size={10} /> {member.role}
              </span>
            )}
            {!isOwner && member.role && (
              <span className="text-[10px] px-1.5 py-0.5 bg-brand-light text-brand-gray font-bold rounded-md">
                {member.role}
              </span>
            )}
            {!member.is_public && (
              <span className="text-[10px] px-1.5 py-0.5 bg-red-50 text-error font-bold rounded-md">
                Hidden
              </span>
            )}
            {member.is_featured_on_home && (
              <span className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-600 font-bold rounded-md">
                Featured
              </span>
            )}
          </div>
          {member.tagline && (
            <p className="text-xs text-brand-gray mt-0.5 line-clamp-1">{member.tagline}</p>
          )}
          <div className="flex items-center gap-2 mt-1 text-[10px] text-ui-muted">
            {member.phone && <span className="flex items-center gap-0.5"><Phone size={10} /> {member.phone}</span>}
            {member.email && <span className="flex items-center gap-0.5"><Mail size={10} /> {member.email}</span>}
            <span>Order: {member.order_index}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0 flex-wrap">
          <button
            onClick={onToggleVisibility}
            className={clsx(
              'flex items-center gap-1 px-2 py-1.5 text-[11px] font-bold rounded-lg transition-colors',
              member.is_public
                ? 'text-success bg-green-50 hover:bg-green-100'
                : 'text-error bg-red-50 hover:bg-red-100'
            )}
            title={member.is_public ? 'Hide from public' : 'Make public'}
          >
            {member.is_public ? <Eye size={12} /> : <EyeOff size={12} />}
            <span className="hidden sm:inline">{member.is_public ? 'Public' : 'Hidden'}</span>
          </button>
          <button
            onClick={onToggleFeatured}
            className={clsx(
              'flex items-center gap-1 px-2 py-1.5 text-[11px] font-bold rounded-lg transition-colors',
              member.is_featured_on_home
                ? 'text-amber-600 bg-amber-50 hover:bg-amber-100'
                : 'text-brand-gray bg-brand-light hover:bg-gray-200'
            )}
            title={member.is_featured_on_home ? 'Remove from home' : 'Feature on home'}
          >
            {member.is_featured_on_home ? <StarOff size={12} /> : <Star size={12} />}
          </button>
          <button
            onClick={onEdit}
            className="flex items-center gap-1 px-2 py-1.5 text-[11px] font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
          >
            <Edit3 size={12} />
            <span className="hidden sm:inline">Edit</span>
          </button>
          <button
            onClick={onDelete}
            className="flex items-center gap-1 px-2 py-1.5 text-[11px] font-bold text-error bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

/* ── Team Form Slide-Over (Create / Edit) ── */
function TeamFormSlideOver({ open, onClose, mode, member }) {
  const createMutation = useCreateTeamMember();
  const updateMutation = useUpdateTeamMember();
  const mutation = mode === 'create' ? createMutation : updateMutation;

  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [socialInput, setSocialInput] = useState({ platform: '', url: '' });
  const [initialized, setInitialized] = useState(null);
  const fileRef = useRef(null);

  // Re-init form when member prop changes (edit mode)
  if (mode === 'edit' && member && initialized !== member.id) {
    setForm({
      full_name: member.full_name || '',
      role: member.role || '',
      tagline: member.tagline || '',
      bio: member.bio || '',
      phone: member.phone || '',
      whatsapp: member.whatsapp || '',
      email: member.email || '',
      social_links: member.social_links || [],
      is_public: member.is_public ?? true,
      is_featured_on_home: member.is_featured_on_home ?? false,
    });
    setImagePreview(member.image_path ? getImageUrl(member.image_path) : null);
    setImageFile(null);
    setInitialized(member.id);
  }

  // Reset on open create
  if (mode === 'create' && open && initialized !== 'create') {
    setForm(EMPTY_FORM);
    setImageFile(null);
    setImagePreview(null);
    setInitialized('create');
  }

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be under 5MB');
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const addSocialLink = () => {
    if (!socialInput.platform || !socialInput.url) return;
    setForm((f) => ({
      ...f,
      social_links: [...f.social_links, { ...socialInput }],
    }));
    setSocialInput({ platform: '', url: '' });
  };

  const removeSocialLink = (index) => {
    setForm((f) => ({
      ...f,
      social_links: f.social_links.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      let image_path = member?.image_path || null;

      if (mode === 'create') {
        // Create first to get the id, then upload image
        const payload = { ...form };
        const created = await createMutation.mutateAsync(payload);

        if (imageFile && created?.id) {
          image_path = await uploadTeamImage(imageFile, created.id);
          await updateMutation.mutateAsync({ id: created.id, updates: { image_path } });
        }
      } else {
        // Edit mode
        if (imageFile && member?.id) {
          image_path = await uploadTeamImage(imageFile, member.id);
        }
        await updateMutation.mutateAsync({
          id: member.id,
          updates: { ...form, image_path },
        });
      }

      handleClose();
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setInitialized(null);
    setImageFile(null);
    setImagePreview(null);
    setSocialInput({ platform: '', url: '' });
    onClose();
  };

  const isPending = mutation.isPending || uploading;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="team-form-overlay"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={handleClose}
            className="fixed inset-0 z-40 bg-brand-dark/40 backdrop-blur-sm"
          />
          <motion.div
            key="team-form-panel"
            variants={panelVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-brand-white shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-ui-border flex-shrink-0">
              <div>
                <h2 className="text-lg font-extrabold text-brand-dark">
                  {mode === 'create' ? 'Add Team Member' : 'Edit Team Member'}
                </h2>
                {mode === 'edit' && member && (
                  <p className="text-xs text-brand-gray mt-0.5">{member.full_name}</p>
                )}
              </div>
              <button onClick={handleClose} className="p-2 rounded-lg text-brand-gray hover:bg-brand-light transition-colors">
                <X size={20} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
              {/* Image upload */}
              <div>
                <label className="block text-sm font-semibold text-brand-dark mb-2">Profile Photo</label>
                <div className="flex items-center gap-4">
                  <div
                    onClick={() => fileRef.current?.click()}
                    className="w-24 h-24 rounded-2xl overflow-hidden bg-brand-light border-2 border-dashed border-ui-border hover:border-brand-red/40 cursor-pointer transition-colors flex items-center justify-center group"
                  >
                    {imagePreview ? (
                      <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center">
                        <ImagePlus size={24} className="text-ui-muted group-hover:text-brand-red mx-auto transition-colors" />
                        <p className="text-[10px] text-ui-muted mt-1">Upload</p>
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-brand-gray space-y-1">
                    <p>Max 5MB · JPEG, PNG, WebP</p>
                    <p>Recommended: 400×400 or larger</p>
                    {imageFile && (
                      <p className="text-brand-dark font-medium">{imageFile.name}</p>
                    )}
                  </div>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-brand-dark mb-1.5">Full Name *</label>
                <input
                  type="text"
                  value={form.full_name}
                  onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                  placeholder="e.g. John Doe"
                  className="w-full px-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
                  required
                />
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-semibold text-brand-dark mb-1.5">Role / Title *</label>
                <input
                  type="text"
                  value={form.role}
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                  placeholder="e.g. Owner, Manager, Chef"
                  className="w-full px-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
                  required
                />
              </div>

              {/* Tagline */}
              <div>
                <label className="block text-sm font-semibold text-brand-dark mb-1.5">
                  Tagline
                  <span className="text-xs text-brand-gray ml-2">(short — for card display)</span>
                </label>
                <input
                  type="text"
                  value={form.tagline}
                  onChange={(e) => setForm((f) => ({ ...f, tagline: e.target.value }))}
                  placeholder="e.g. Passionate about quality food"
                  className="w-full px-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
                  maxLength={120}
                />
              </div>

              {/* Bio */}
              <div>
                <label className="block text-sm font-semibold text-brand-dark mb-1.5">Bio</label>
                <textarea
                  rows={4}
                  value={form.bio}
                  onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
                  placeholder="Write about this team member..."
                  className="w-full px-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted resize-none"
                />
              </div>

              {/* Contact fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-brand-dark mb-1.5">
                    <Phone size={12} className="inline mr-1" /> Phone
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="+254 700 000 000"
                    className="w-full px-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-brand-dark mb-1.5">
                    <MessageCircle size={12} className="inline mr-1" /> WhatsApp
                  </label>
                  <input
                    type="tel"
                    value={form.whatsapp}
                    onChange={(e) => setForm((f) => ({ ...f, whatsapp: e.target.value }))}
                    placeholder="+254 700 000 000"
                    className="w-full px-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-brand-dark mb-1.5">
                  <Mail size={12} className="inline mr-1" /> Email
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="name@example.com"
                  className="w-full px-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
                />
              </div>

              {/* Social links */}
              <div>
                <label className="block text-sm font-semibold text-brand-dark mb-2">Social Links</label>
                {form.social_links.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {form.social_links.map((link, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-2 bg-brand-light rounded-xl">
                        <ExternalLink size={12} className="text-brand-gray flex-shrink-0" />
                        <span className="text-xs font-semibold text-brand-dark">{link.platform}</span>
                        <span className="text-xs text-brand-gray truncate flex-1">{link.url}</span>
                        <button
                          type="button"
                          onClick={() => removeSocialLink(i)}
                          className="p-0.5 text-error hover:text-red-700 transition-colors"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={socialInput.platform}
                    onChange={(e) => setSocialInput((s) => ({ ...s, platform: e.target.value }))}
                    placeholder="Platform"
                    className="w-28 px-3 py-2 text-xs bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
                  />
                  <input
                    type="url"
                    value={socialInput.url}
                    onChange={(e) => setSocialInput((s) => ({ ...s, url: e.target.value }))}
                    placeholder="https://..."
                    className="flex-1 px-3 py-2 text-xs bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
                  />
                  <button
                    type="button"
                    onClick={addSocialLink}
                    disabled={!socialInput.platform || !socialInput.url}
                    className="px-3 py-2 text-xs font-bold text-brand-white bg-brand-red hover:bg-red-700 disabled:bg-brand-light disabled:text-brand-gray rounded-xl transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Visibility toggles */}
              <div className="space-y-3 pt-2 border-t border-ui-border">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_public}
                    onChange={(e) => setForm((f) => ({ ...f, is_public: e.target.checked }))}
                    className="w-4 h-4 rounded border-ui-border text-brand-red focus:ring-brand-red/20"
                  />
                  <div>
                    <span className="text-sm font-medium text-brand-dark">Visible on public site</span>
                    <p className="text-xs text-brand-gray">Show this member on the public team page</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_featured_on_home}
                    onChange={(e) => setForm((f) => ({ ...f, is_featured_on_home: e.target.checked }))}
                    className="w-4 h-4 rounded border-ui-border text-brand-red focus:ring-brand-red/20"
                  />
                  <div>
                    <span className="text-sm font-medium text-brand-dark">Featured on homepage</span>
                    <p className="text-xs text-brand-gray">Show in the "Meet the Team" carousel on home</p>
                  </div>
                </label>
              </div>
            </form>

            {/* Footer actions */}
            <div className="px-6 py-4 border-t border-ui-border flex items-center gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={(e) => {
                  e.currentTarget.closest('.fixed').querySelector('form').requestSubmit();
                }}
                disabled={isPending || !form.full_name || !form.role}
                className={clsx(
                  'flex items-center gap-2 px-6 py-2.5 text-sm font-bold rounded-xl transition-all',
                  isPending || !form.full_name || !form.role
                    ? 'bg-brand-red/60 text-brand-white cursor-not-allowed'
                    : 'bg-brand-red hover:bg-red-700 text-brand-white shadow-lg'
                )}
              >
                {isPending && <Loader2 size={14} className="animate-spin" />}
                {mode === 'create' ? 'Add Member' : 'Save Changes'}
              </button>
              <button
                onClick={handleClose}
                className="px-6 py-2.5 text-sm font-bold text-brand-gray bg-brand-light hover:bg-gray-200 rounded-xl transition-colors"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
